import { Suspense, useState } from 'react';
import { Skeleton } from '@mui/material';
import Button from '@mui/material/Button';
import { Column, Row } from 'further-ui/components/Layout';
import { CalendarDay, downloadFileFromTrpc } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import { CurrencySettings } from '../../types';
import PcapDateSelector from './PcapDateSelector';
import PcapTable from './PcapTable';

type Props = {
  investorId: string;
  commitmentId?: string;
  vehicleId: string;
  currencySettings: CurrencySettings;
  investorName: string;
};

const PcapContent: React.FC<
  Props & { selectedDate: CalendarDay; vehicleName?: string }
> = ({
  investorId,
  commitmentId,
  vehicleId,
  currencySettings,
  investorName,
  vehicleName,
  selectedDate,
}) => {
  const [pcapData] = api.generalLedger.getPcap.useSuspenseQuery({
    vehicleId,
    investorId,
    ...(commitmentId ? { commitmentId } : {}),
    endDate: selectedDate,
  });

  return (
    <PcapTable
      pcap={pcapData}
      currency={currencySettings.currencyLcy}
      investorName={investorName}
      vehicleName={vehicleName}
    />
  );
};

type PcapSectionContentProps = Props & {
  initialDate: CalendarDay;
};

const PcapSectionContent: React.FC<PcapSectionContentProps> = ({
  investorId,
  commitmentId,
  vehicleId,
  currencySettings,
  investorName,
  initialDate,
}) => {
  const { vehicle } = useLayoutContext();
  const { error } = useNotification();
  const [selectedDate, setSelectedDate] = useState(initialDate);

  const { mutateAsync: handleDownload, isPending } =
    api.generalLedger.downloadPcap.useMutation(
      useHandleTrpcMutation()({ invalidations: [] }),
    );

  const handleDownloadPcap = async () => {
    const data = await handleDownload({
      vehicleId,
      investorId,
      ...(commitmentId ? { commitmentId } : {}),
      endDate: selectedDate,
    });
    downloadFileFromTrpc(data, () => error('Failed to download PCAP.'));
  };

  return (
    <Column spacing="md" fullWidth>
      <Row justify="space-between" centered fullWidth>
        <PcapDateSelector
          selectedDate={selectedDate}
          onChange={setSelectedDate}
        />
        <Button
          variant="contained"
          size="large"
          loading={isPending}
          onClick={handleDownloadPcap}
        >
          Download PCAP
        </Button>
      </Row>
      <Suspense fallback={<Skeleton height={400} />}>
        <PcapContent
          investorId={investorId}
          commitmentId={commitmentId}
          vehicleId={vehicleId}
          currencySettings={currencySettings}
          investorName={investorName}
          vehicleName={vehicle?.vehicleName}
          selectedDate={selectedDate}
        />
      </Suspense>
    </Column>
  );
};

const PcapSection: React.FC<Props> = (props) => {
  const [periods] = api.accountingPeriod.listPeriods.useSuspenseQuery({
    vehicleId: props.vehicleId,
  });

  if (!periods.length) return null;

  return <PcapSectionContent {...props} initialDate={periods[0].periodEnd} />;
};

export default PcapSection;
